/*
 * Copyright (C) 2018 - 2026 Entgra (Pvt) Ltd, Inc - All Rights Reserved.
 *
 * Unauthorised copying/redistribution of this file, via any medium is strictly prohibited.
 *
 * Licensed under the Entgra Commercial License, Version 1.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *       https://entgra.io/licenses/entgra-commercial/1.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import { lazy, Suspense, type ReactNode } from 'react';
import { createBrowserRouter, Navigate, type RouteObject } from 'react-router-dom';
import { Center, Loader } from '@mantine/core';
import appConfig from '../../../ui-config.json';
import { RequireAuth } from './RequireAuth';
import { AppShell } from './AppShell';
import { RouteErrorBoundary } from './RouteErrorBoundary';

export const ROUTES = {
  login: '/login',
  dashboard: '/dashboard',
  meterInventory: '/meter-inventory',
  meterAnalytics: '/meter-analytics',
  meterHierarchy: '/meter-hierarchy',
  meterHierarchyConfiguration: '/meter-hierarchy-configuration',
  equipment: '/equipment',
} as const;

const LoginPage = lazy(() => import('../scenes/Login'));
const DashboardPage = lazy(() => import('../scenes/Dashboard'));
const MeterInventoryPage = lazy(() => import('../scenes/MeterInventory'));
const MeterHierarchyPage = lazy(() => import('../scenes/MeterHierarchy'));
const MeterHierarchyConfigurationPage = lazy(() => import('../scenes/MeterHierarchyConfiguration'));
const EquipmentPage = lazy(() => import('../scenes/Equipment'));
const MeterAnalyticsPage = lazy(() => import('../scenes/MeterAnalytics'));

function RouteFallback() {
  return (
    <Center h="60vh">
      <Loader />
    </Center>
  );
}

function withSuspense(element: ReactNode) {
  return <Suspense fallback={<RouteFallback />}>{element}</Suspense>;
}

export const routes: RouteObject[] = [
  {
    path: ROUTES.login,
    element: withSuspense(<LoginPage />),
    errorElement: <RouteErrorBoundary />,
  },
  {
    path: '/',
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      { index: true, element: <Navigate to={ROUTES.dashboard} replace /> },
      { path: ROUTES.dashboard.slice(1), element: withSuspense(<DashboardPage />) },
      { path: ROUTES.meterInventory.slice(1), element: withSuspense(<MeterInventoryPage />) },
      { path: ROUTES.meterAnalytics.slice(1), element: withSuspense(<MeterAnalyticsPage />) },
      { path: ROUTES.meterHierarchy.slice(1), element: withSuspense(<MeterHierarchyPage />) },
      {
        path: ROUTES.meterHierarchyConfiguration.slice(1),
        element: withSuspense(<MeterHierarchyConfigurationPage />),
      },
      { path: ROUTES.equipment.slice(1), element: withSuspense(<EquipmentPage />) },
      { path: '*', element: <Navigate to={ROUTES.dashboard} replace /> },
    ],
  },
];

export const router = createBrowserRouter(routes, { basename: `/${appConfig.appName}` });
